import React, { useMemo, useState } from "react";

import { BarChart as BarChartIcon } from "@mui/icons-material";

import { ClipLoader } from "react-spinners";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import getDestinationIcon from "helpers/destinations/getDestinationIcon";

import {
    ChartArea,
    ChartWrapper,
    EmptyStateContainer,
    EmptyStateDescription,
    EmptyStateIconWrapper,
    EmptyStateTitle,
    LegendItem,
    LegendList,
} from "./VolumeErrorChart.styled";

/**
 * Ten series colours, so a tracker with ten destinations gets a distinct
 * colour per pixel instead of wrapping. Red is deliberately absent: it is
 * reserved for the errors series.
 */
const DEFAULT_COLORS = [
    "#0066FF",
    "#8B5CF6",
    "#F66700",
    "#FFA800",
    "#10B981",
    "#06B6D4",
    "#EC4899",
    "#F59E0B",
    "#64748B",
    "#84CC16",
];

const ERRORS_KEY = "errors";
const ERRORS_LABEL = "Errors";

const getDestCode = (key) => {
    if (key.split("|").length > 1) {
        return key.split("|")[1] !== "+" ? key.split("|")[1] : "";
    }
    return key;
};

const getDestName = (key) => {
    if (key.split("|").length > 1) {
        return key.split("|")[0];
    }
    return key;
};

const getDestinationIconHandler = (key) => {
    const code = getDestCode(key);
    if (code !== "") {
        return getDestinationIcon(code);
    }
    return "";
};

const VolumeErrorChart = ({
    chartData,
    destinationList = [],
    colors = DEFAULT_COLORS,
    loader = false,
}) => {
    const [hiddenKeys, setHiddenKeys] = useState(new Set());
    const [animatingKey, setAnimatingKey] = useState(null);

    const { data, barKeys } = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return { data: [], barKeys: ["Total"] };
        }

        const availableKeys = Object.keys(chartData[0]).filter(
            (x) => x !== "name" && x !== "Total" && x !== "errors",
        );
        const keys = availableKeys.length >= 1 ? availableKeys : ["Total"];

        const rows = chartData.map((item) => {
            const errors = item.errors || 0;
            let total = item.Total;
            if (total === undefined || total === null) {
                total = keys.reduce((sum, k) => sum + (item[k] || 0), 0);
            }

            const row = { name: item.name };
            keys.forEach((k) => {
                if (item[k] !== undefined) {
                    row[k] = item[k];
                }
            });
            row.Total = total || 0;
            row[ERRORS_KEY] = errors;
            return row;
        });

        return { data: rows, barKeys: keys };
    }, [chartData]);

    const toggleKey = (dataKey) => {
        setAnimatingKey(dataKey);
        window.setTimeout(() => setAnimatingKey(null), 450);

        setHiddenKeys((prev) => {
            const next = new Set(prev);
            if (next.has(dataKey)) {
                next.delete(dataKey);
            } else {
                next.add(dataKey);
            }
            return next;
        });
    };

    const legendItems = useMemo(
        () => [
            ...barKeys.map((key, index) => ({
                dataKey: key,
                value: key,
                color: colors[index % colors.length],
            })),
            {
                dataKey: ERRORS_KEY,
                value: ERRORS_LABEL,
                color: "red",
            },
        ],
        [barKeys, colors],
    );

    const visibleBarKeys = barKeys.filter((key) => !hiddenKeys.has(key));
    const isErrorsVisible = !hiddenKeys.has(ERRORS_KEY);
    const yAxisWidth = useMemo(() => {
        const seriesKeys = [...barKeys, ERRORS_KEY];
        const maxValue = data.reduce(
            (currentMax, row) =>
                seriesKeys.reduce((rowMax, key) => {
                    const value = Number(row[key]);
                    return Number.isFinite(value)
                        ? Math.max(rowMax, Math.abs(value))
                        : rowMax;
                }, currentMax),
            0,
        );

        // Recharts defaults to 60px, which clips seven-digit (and larger)
        // labels. Reserve space from the longest value plus tick padding.
        return Math.max(60, String(Math.ceil(maxValue)).length * 8 + 24);
    }, [barKeys, data]);

    const renderLegend = () => {
        const hasDestinations = destinationList.length > 0;

        return (
            <LegendList>
                {legendItems.map((item) => {
                    const isVisible = !hiddenKeys.has(item.dataKey);

                    return (
                        <LegendItem
                            key={`item-${item.dataKey}`}
                            data-key={item.value}
                            $isVisible={isVisible}
                            $isAnimating={animatingKey === item.dataKey}
                            $color={item.color}
                            onClick={() => toggleKey(item.dataKey)}
                            TouchRippleProps={{
                                style: { color: item.color },
                            }}
                        >
                            <span className="square" />
                            {hasDestinations &&
                                getDestinationIconHandler(item.value) !==
                                    "" && (
                                    <span className="dest-icon">
                                        <img
                                            width={16}
                                            src={getDestinationIconHandler(
                                                item.value,
                                            )}
                                            alt=""
                                        />
                                    </span>
                                )}
                            <span className="title">
                                {item.value === ERRORS_LABEL
                                    ? ERRORS_LABEL
                                    : getDestName(item.value)}
                            </span>
                        </LegendItem>
                    );
                })}
            </LegendList>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || !payload.length) return null;

        const row = payload[0]?.payload;
        if (!row) return null;

        return (
            <div className="custom-chart-tooltip">
                <p className="label">{row.name}</p>
                {visibleBarKeys.map((key) => (
                    <p className="label" key={key}>
                        {`${getDestName(key)} : ${row[key] ?? 0}`}
                    </p>
                ))}
                {isErrorsVisible && (
                    <p className="label">{`Total Errors : ${row[ERRORS_KEY] ?? 0}`}</p>
                )}
            </div>
        );
    };

    const isEmpty =
        !loader &&
        (!data ||
            data.length === 0 ||
            data.every((item) =>
                Object.entries(item).every(
                    ([key, value]) =>
                        key === "name" ||
                        value === null ||
                        value === undefined ||
                        value === 0 ||
                        value === "",
                ),
            ));

    const EmptyState = () => (
        <EmptyStateContainer>
            <EmptyStateIconWrapper>
                <BarChartIcon />
            </EmptyStateIconWrapper>
            <EmptyStateTitle>No Data Available</EmptyStateTitle>
            <EmptyStateDescription>
                There's no data to display in the chart. Data will appear here
                once leads are generated.
            </EmptyStateDescription>
        </EmptyStateContainer>
    );

    return (
        <ChartWrapper>
            {loader && (
                <div className="chart-loader">
                    <ClipLoader color="#000000" size={35} />
                </div>
            )}
            {isEmpty ? (
                <EmptyState />
            ) : (
                <>
                    <ChartArea>
                        <ResponsiveContainer>
                            <AreaChart
                                data={data}
                                margin={{
                                    top: 10,
                                    right: 30,
                                    left: 8,
                                    bottom: 0,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis
                                    allowDecimals={false}
                                    domain={[0, "auto"]}
                                    width={yAxisWidth}
                                />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={false}
                                />
                                {barKeys.map((key, index) => {
                                    const color = colors[index % colors.length];
                                    return (
                                        <Area
                                            key={key}
                                            type="monotone"
                                            dataKey={key}
                                            stroke={color}
                                            fill={color}
                                            fillOpacity={0.3}
                                            hide={hiddenKeys.has(key)}
                                        />
                                    );
                                })}
                                <Area
                                    type="monotone"
                                    dataKey={ERRORS_KEY}
                                    stroke="red"
                                    fill="red"
                                    fillOpacity={0}
                                    strokeWidth={1}
                                    dot={{ r: 2 }}
                                    name={ERRORS_LABEL}
                                    hide={hiddenKeys.has(ERRORS_KEY)}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartArea>
                    {renderLegend()}
                </>
            )}
        </ChartWrapper>
    );
};

export default VolumeErrorChart;
